import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  type CalculateMonthlyContributionParams,
  calculateMonthlyContribution,
  calculateMonthlyContributionWithFee,
  calculateMonthlyContributionWithoutFee,
  calculateTargetAmount,
} from "Charts/Utils/CommonChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { ConfirmRecalculateTargetModal } from "Components/_Shared/Dialogs/ConfirmRecalculateTargetModal";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { FormTextarea } from "Forms/Components/FormTextarea";
import {
  type OtherGoalFormType as CreateOtherGoalFormType,
  DrawType,
  getOtherGoalFormSchema,
  minDate,
} from "Forms/Schemas/Goals/otherGoalFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetOtherGoalQueryKey,
  useCreateOtherGoal,
  useUpdateOtherGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  OtherGoalDataPayoutFrequency,
  type OtherGoalDto,
  OtherGoalDtoPriorityLevel,
  TimeRangeType,
} from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_INFLATION_RATE, INTEREST_RATE_AGGRESIVE, INTEREST_RATE_CONSERVATIVE } from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInMonths, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { OtherGoalModelingHint } from "./OtherGoalModelingHint";
import { SavingsChart } from "./SavingsChart";

type Props = {
  readonly onClose: () => void;
  readonly onDrawTypeChange: (drawType: DrawType) => void;
  readonly editGoal?: OtherGoalDto;
  readonly goalId?: string;
};

export const OtherGoalOneTimeForm = forwardRef<{}, Props>(({ onDrawTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const defaultValues = useMemo(() => {
    if (editGoal) {
      return {
        includeFee: editGoal.modeling.fee?.enabled,
        includeInflation: editGoal.modeling.inflation?.enabled,
        feeRate: Number(editGoal.modeling.fee?.rate) || 0,
        inflationRate: Number(editGoal.modeling.inflation?.rate) || 0,
        name: editGoal.name,
        drawStart: new Date(editGoal.data.targetDate as string),
        targetAmount: Number(editGoal.data.payoutAmount?.value) || 0,
        interestRate: Number(editGoal.modeling.appreciationRate) || 0,
        note: editGoal.note || "",
        priority: editGoal.priorityLevel === OtherGoalDtoPriorityLevel.high,
        drawType: DrawType.LumpSum,
        referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
      };
    }

    return {
      includeFee: true,
      includeInflation: true,
      feeRate: DEFAULT_INFLATION_RATE,
      inflationRate: DEFAULT_INFLATION_RATE,
      name: t(Resources.AnalysisDetail.Goals.GoalType.Other),
      monthlyContribution: 0,
      monthlyContributionWithFee: 0,
      interestRate: INTEREST_RATE_CONSERVATIVE,
      drawType: DrawType.LumpSum,
      referenceStartDate: new Date(),
    };
  }, [editGoal, t]);

  const methods = useForm<CreateOtherGoalFormType>({
    resolver: yupResolver(getOtherGoalFormSchema(t)),
    mode: "onBlur",
    defaultValues,
  });

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = methods;

  const drawType = watch("drawType");
  const drawStart = watch("drawStart");
  const targetAmount = watch("targetAmount");
  const feeRate = watch("feeRate");
  const monthlyContribution = watch("monthlyContribution");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const referenceStartDate = watch("referenceStartDate");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingMonthlyContributionWithFee, setPendingMonthlyContributionWithFee] = useState<number | null>(null);
  const [lastAutoMonthlyContributionWithFee, setLastAutoMonthlyContributionWithFee] = useState<number | null>(null);

  const investmentLength = differenceInMonths(drawStart, new Date());
  const isInvalidDrawStartDate = isBefore(startOfDay(drawStart), startOfDay(minDate));
  const shouldRenderChart = drawStart && !isInvalidDrawStartDate && monthlyContribution > 0 && targetAmount > 0;

  const { mutate: createOtherGoal } = useCreateOtherGoal();

  const { mutate: updateOtherGoal } = useUpdateOtherGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
  }));

  useEffect(() => {
    if (editGoal) {
      return;
    }

    if (investmentLength && investmentLength > 120) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE);
    }
  }, [setValue, investmentLength, editGoal]);

  useEffect(() => {
    if (!drawStart || isInvalidDrawStartDate) return;
    const durationInMonths = drawStart ? differenceInMonths(drawStart, new Date()) : 0;
    if (interestRate !== undefined && targetAmount !== undefined && durationInMonths !== undefined) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      const wf = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);

      const roundedWf = Math.round(Number(wf));
      setValue("monthlyContribution", monthlyContribution.toString() as any);
      setValue("monthlyContributionWithFee", roundedWf as any);
      setLastAutoMonthlyContributionWithFee(roundedWf);
    }
  }, [feeRate, inflationRate, interestRate, targetAmount, includeInflation, setValue, drawStart, isInvalidDrawStartDate]);

  useEffect(() => {
    if (!drawStart || isInvalidDrawStartDate) return;
    clearErrors("drawStart");
    onChangeDrawStartDate();
  }, [drawStart, isInvalidDrawStartDate, clearErrors]);

  const onChangeDrawStartDate = () => {
    onChange({
      durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
      targetAmount,
      inflationRate,
      interestRate,
    });
  };

  const onChange = ({ inflationRate, interestRate, targetAmount, durationInMonths }: CalculateMonthlyContributionParams) => {
    if (interestRate !== undefined && targetAmount !== undefined && durationInMonths !== undefined) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount: Number(targetAmount) || 0,
        durationInMonths: Number(durationInMonths) || 0,
        interestRate: Number(interestRate) || 0,
        inflationRate: Number(inflationRate),
      });

      const wf = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);

      const roundedWf = Math.round(Number(wf));
      setValue("monthlyContribution", monthlyContribution.toString() as any);
      setValue("monthlyContributionWithFee", roundedWf as any);
      setLastAutoMonthlyContributionWithFee(roundedWf);
    }
  };
  const recalculateMonthlyContributionFromTarget = () => {
    const durationInMonths = drawStart ? differenceInMonths(drawStart, new Date()) : 0;
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const wf = calculateMonthlyContributionWithFee(Number(monthlyContribution), Number(includeFee ? feeRate : 0));
    const roundedWf = Math.round(Number(wf));
    setValue("monthlyContribution", monthlyContribution.toString() as any);
    setValue("monthlyContributionWithFee", roundedWf as any);
    setLastAutoMonthlyContributionWithFee(roundedWf);
  };

  const handleConfirmResult = (shouldRecalculateTarget: boolean) => {
    if (shouldRecalculateTarget && pendingMonthlyContributionWithFee != null) {
      const durationInMonths = drawStart ? differenceInMonths(drawStart, new Date()) : 0;
      const monthsToYears = durationInMonths / 12;
      const contributionWithoutFee = calculateMonthlyContributionWithoutFee(
        Number(pendingMonthlyContributionWithFee),
        Number(includeFee ? feeRate : 0),
      );

      const newTarget = calculateTargetAmount(
        Number(contributionWithoutFee),
        monthsToYears,
        Number(interestRate) || 0,
        includeInflation ? Number(inflationRate) || 0 : 0,
      );

      setValue("targetAmount", Math.round(newTarget) as any);
      setValue("monthlyContribution", contributionWithoutFee.toString() as any);
      const roundedWithFee = Math.round(Number(pendingMonthlyContributionWithFee));
      setValue("monthlyContributionWithFee", roundedWithFee as any);
      setLastAutoMonthlyContributionWithFee(roundedWithFee);
    } else {
      recalculateMonthlyContributionFromTarget();
    }

    setIsConfirmOpen(false);
    setPendingMonthlyContributionWithFee(null);
  };

  const onCreate = (data: CreateOtherGoalFormType) => {
    createOtherGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? OtherGoalDtoPriorityLevel.high : OtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.drawStart, "yyyy-MM-dd"),
            payoutFrequency: OtherGoalDataPayoutFrequency.one_time,
            payoutInterval: {
              value: 0,
              type: TimeRangeType.month,
            },
            payoutAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            targetAmount: {
              value: 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            regularInvestmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            appreciationRate: data.interestRate,
            payoutAppreciationRate: 0,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEdit = (data: CreateOtherGoalFormType) => {
    updateOtherGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? OtherGoalDtoPriorityLevel.high : OtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.drawStart, "yyyy-MM-dd"),
            payoutFrequency: OtherGoalDataPayoutFrequency.one_time,
            payoutInterval: {
              value: 0,
              type: TimeRangeType.month,
            },
            payoutAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            targetAmount: {
              value: 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            regularInvestmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            appreciationRate: data.interestRate,
            payoutAppreciationRate: 0,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetOtherGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <ConfirmRecalculateTargetModal isOpen={isConfirmOpen} handleResult={handleConfirmResult} />
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Other)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlCheckboxInput
                  control={control}
                  disabled={readOnly}
                  name="priority"
                  text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                />
              </Grid>
              <Grid size={12}>
                <BlRadioButtons
                  control={control}
                  name="drawType"
                  label={t(Resources.AnalysisDetail.Goals.DrawType)}
                  onChange={(drawType) => onDrawTypeChange(drawType === DrawType.LumpSum.toString() ? DrawType.LumpSum : DrawType.Monthly)}
                  disabled={!!editGoal || readOnly}
                  codeList={[
                    {
                      code: DrawType.LumpSum,
                      name: t(Resources.AnalysisDetail.Goals.LumpSum),
                    },
                    {
                      code: DrawType.Monthly,
                      name: t(Resources.AnalysisDetail.Goals.Monthly),
                    },
                  ]}
                  errors={errors}
                />
              </Grid>
              {drawType === DrawType.LumpSum && (
                <>
                  <Grid size={6}>
                    <DatePicker
                      control={control}
                      disabled={readOnly}
                      name="drawStart"
                      label={t(Resources.AnalysisDetail.Goals.DrawDate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      minDate={startOfMonth(addMonths(new Date(), 1))}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="targetAmount"
                      label={t(Resources.AnalysisDetail.Goals.DrawAmount)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      onChange={(targetAmount) =>
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount: Number(targetAmount),
                          inflationRate,
                          interestRate,
                        })
                      }
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          min: 0,
                          scale: 0,
                        },
                      ]}
                      unmask="typed"
                      inputEndAdornment={
                        <Typography component="span" variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
          {targetAmount > 0 && drawStart && !isInvalidDrawStartDate && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={12}>
                    <OtherGoalModelingHint
                      startDate={drawStart}
                      interestRate={interestRate}
                      targetAmount={targetAmount}
                      monthlyContribution={monthlyContributionWithFee}
                      referenceStartDate={referenceStartDate}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 0,
                          min: 0,
                        },
                      ]}
                      unmask="typed"
                      inputEndAdornment={
                        <Typography component="span" variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                      onChange={(newContributionWithFee) => {
                        setValue("monthlyContribution", calculateMonthlyContributionWithoutFee(Number(newContributionWithFee), feeRate));
                      }}
                      onBlur={() => {
                        const currentValue = Math.round(Number(monthlyContributionWithFee));
                        if (lastAutoMonthlyContributionWithFee !== null && currentValue === Number(lastAutoMonthlyContributionWithFee)) {
                          return;
                        }
                        setPendingMonthlyContributionWithFee(currentValue);
                        setIsConfirmOpen(true);
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 1,
                          min: includeInflation ? 0 : 0.1,
                        },
                      ]}
                      unmask="typed"
                      inputEndAdornment={
                        <Typography component="span" variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      onChange={(newInterestRate) => {
                        if (
                          (includeInflation && Number(newInterestRate) === Number(inflationRate)) ||
                          (!includeInflation && Number(newInterestRate) <= 0)
                        ) {
                          setValue("interestRate", interestRate);
                          return;
                        }
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate: Number(newInterestRate),
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      disabled={Number(interestRate) === Number(inflationRate) || readOnly}
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        if (newInflationRate === Number(interestRate)) {
                          setValue("inflationRate", (newInflationRate + 0.1).toString() as any);
                        } else {
                          setValue("inflationRate", newInflationRate.toString() as any);
                        }

                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate: newInflationRate,
                          interestRate,
                        });
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeInflation || readOnly}
                      control={control}
                      name="inflationRate"
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 1,
                          min: 0,
                          max: 20,
                        },
                      ]}
                      unmask="typed"
                      onChange={(newInflationRate) => {
                        if (Number(newInflationRate) === Number(interestRate)) {
                          setValue("inflationRate", inflationRate);
                          return;
                        }
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate: Number(newInflationRate),
                          interestRate,
                        });
                      }}
                      inputEndAdornment={
                        <Typography component="span" variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        setValue("feeRate", newFeeRate);
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate,
                        });
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeFee || readOnly}
                      control={control}
                      name="feeRate"
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 1,
                          min: 0,
                          max: 20,
                        },
                      ]}
                      unmask="typed"
                      inputEndAdornment={
                        <Typography component="span" variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderChart && (
                <Grid size={12}>
                  <Stack spacing={Spacing.XS} padding={Spacing.S} display="flex" justifyContent="center" alignItems="center">
                    <Typography component="span" variant="caption" fontWeight={500}>
                      {t(Resources.Common.Investment)}
                    </Typography>
                    <Typography component="span" variant="caption">
                      {formatCurrency(Math.round(Number(monthlyContributionWithFee)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                    </Typography>
                  </Stack>
                  <SavingsChart
                    referenceStartDate={referenceStartDate}
                    targetDate={drawStart}
                    targetAmount={targetAmount}
                    monthlyContributionAmount={Number(monthlyContribution)}
                    interestRate={interestRate}
                    inflationRate={inflationRate}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
